import {
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
	type Row,
	type ColumnDef,
} from "@tanstack/react-table";
import { useState } from "react";
import { BiDownArrowAlt, BiUpArrowAlt } from 'react-icons/bi'
import { FastProject } from "@typedefs/interfaces";
import { useAppSelector } from "@app/hooks";
import { selectActiveCopyJobForNewProject } from "@app/features/asyncJobs/asyncJobsSlice";
import Spinner from "@components/spinner/Spinner";

interface ProjectRowProps {
	row: Row<FastProject>;
}

const ProjectRow = ({ row }: ProjectRowProps) => {
	const projectId = row.original.id;
	const isFinalized = row.original.is_finalized;
	
	const activeCopyJob = useAppSelector(state => 
		selectActiveCopyJobForNewProject(state, projectId)
	);
	const isCopying = !!activeCopyJob;

	return (
		<tr 
			key={row.id} 
			className="pos-relative"
			style={isCopying ? { opacity: 0.5 } : {}}
		>
			{row.getVisibleCells().map((cell) => (
				<td
					key={cell.id}
					colSpan={cell.column.id === "name" || cell.column.id === "updated_at" ? 2 : 1}
				>
					<div className="cell-content">
						{cell.column.id === "edit" ? (
							isCopying ? (
								<div style={{ display: 'flex', justifyContent: 'center' }}>
									<Spinner size={20} isBlack />
								</div>
							) : (
								flexRender(cell.column.columnDef.cell, cell.getContext())
							)
						) : (
							<div style={isFinalized ? { opacity: 0.5 } : {}}>
								{flexRender(cell.column.columnDef.cell, cell.getContext())}
							</div>
						)}
					</div>
				</td>
			))}
		</tr>
	);
};

const ProjectSelectTable = ({
	data,
	columns,
}: {
	data: FastProject[];
	columns: ColumnDef<FastProject, any>[];
}) => {
	const [sorting, setSorting] = useState<SortingState>([]);

	const table = useReactTable({
		data,
		columns,
		state: {
			sorting,
		},
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});

	return (
		<table className="select-project-table">
			<thead className="select-project-table-header">
				{table.getHeaderGroups().map((headerGroup) => (
					<tr key={headerGroup.id}>
						{headerGroup.headers.map((header) => (
							<th
								key={header.id}
								onClick={header.column.getToggleSortingHandler()}
								style={{ cursor: 'pointer' }}
								colSpan={header.column.id === "name" || header.column.id === "updated_at" ? 2 : 1}
							>
								{header.isPlaceholder ? null : (
									<div className="d-flex align-items-center pos-relative">
										{flexRender(
											header.column.columnDef.header,
											header.getContext()
										)}
										{{
											asc: <span className="d-flex align-items-center pos-absolute" style={{ top: 0, right: 0 }}><BiUpArrowAlt size={13} color="white" /></span>,
											desc: <span className="d-flex align-items-center pos-absolute" style={{ top: 0, right: 0 }}><BiDownArrowAlt size={13} color="white" /></span>,
										}[header.column.getIsSorted() as string] ?? null}
									</div>
								)}
							</th>
						))}
					</tr>
				))}
			</thead>
			<tbody>
				{table.getRowModel().rows.map((row) => (
					<ProjectRow key={row.id} row={row} />
				))}
			</tbody>
		</table>
	);
};



export default ProjectSelectTable;
